import React, { useMemo, useState } from "react";
import "../../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../../designSystem/FilledButton";
import PrimaryButton from "../../designSystem/OutlinedButton";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";
import CustomSelect from "../../designSystem/CustomSelect";


const initialState = {
  reasonForMortgage: "",
  propertyValue: "",
  deposit: "",
  amountToBorrow: "",
  repaymentType: "",
  mortgageTerm: "25",
};

const reasonOptions = [
  { value: "", label: "Please select" },
  { value: "first_home", label: "First Home" },
  { value: "new_home", label: "New Home" },
  { value: "second_home", label: "Second Home" },
  { value: "remortgage", label: "Remortgage" },
  { value: "mortgage_owned_property", label: "Mortgage Owned Property" },
];

const repaymentOptions = [
  { value: "", label: "Please select" },
  { value: "repayment", label: "Repayment" },
  { value: "interest_only", label: "Interest only" },
];

const sanitizeNumber = (value) => String(value || "").replace(/[^\d.]/g, "");

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const getInterestRateFromLtv = (ltv) => {
  if (ltv <= 60) return 3.5;
  if (ltv <= 75) return 4;
  if (ltv <= 85) return 5;
  if (ltv <= 90) return 5.0;
  return 5.0;
};

const calculateRepaymentMortgage = (loanAmount, annualRate, years) => {
  const principal = Number(loanAmount || 0);
  const monthlyRate = Number(annualRate || 0) / 100 / 12;
  const totalMonths = Number(years || 0) * 12;

  if (!principal || !totalMonths) return 0;
  if (monthlyRate === 0) return principal / totalMonths;

  return (
    (principal * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -totalMonths))
  );
};

const calculateInterestOnlyMortgage = (loanAmount, annualRate) => {
  const principal = Number(loanAmount || 0);
  const monthlyRate = Number(annualRate || 0) / 100 / 12;

  if (!principal) return 0;
  return principal * monthlyRate;
};

export default function HowMuchWillItCostCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [errors, setErrors] = useState([]);

  const propertyValueNum = Number(formData.propertyValue || 0);
  const depositNum = Number(formData.deposit || 0);

  const derivedBorrowAmount = useMemo(() => {
    const value = propertyValueNum - depositNum;
    return value > 0 ? value : 0;
  }, [propertyValueNum, depositNum]);

  const amountToBorrowNum = derivedBorrowAmount;
  const ltv =
    propertyValueNum > 0 ? (amountToBorrowNum / propertyValueNum) * 100 : 0;
  const interestRate = getInterestRateFromLtv(ltv);

  const monthlyPayment = useMemo(() => {
    if (!hasCalculated) return 0;

    if (formData.repaymentType === "interest_only") {
      return calculateInterestOnlyMortgage(amountToBorrowNum, interestRate);
    }

    return calculateRepaymentMortgage(
      amountToBorrowNum,
      interestRate,
      Number(formData.mortgageTerm || 0),
    );
  }, [
    hasCalculated,
    formData.repaymentType,
    formData.mortgageTerm,
    amountToBorrowNum,
    interestRate,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mortgageTerm") {
      const cleaned = sanitizeNumber(value);
      setFormData((prev) => ({
        ...prev,
        [name]: cleaned,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const validate = () => {
    const nextErrors = [];

    if (!formData.reasonForMortgage) {
      nextErrors.push("Please select a reason for mortgage.");
    }

    if (!propertyValueNum || propertyValueNum <= 0) {
      nextErrors.push("Please enter the property value.");
    }

    if (depositNum < 0) {
      nextErrors.push("Deposit cannot be less than 0.");
    }

    if (depositNum >= propertyValueNum && propertyValueNum > 0) {
      nextErrors.push("Deposit must be less than the property value.");
    }

    if (!amountToBorrowNum || amountToBorrowNum <= 0) {
      nextErrors.push("Amount to borrow must be greater than 0.");
    }

    if (!formData.repaymentType) {
      nextErrors.push("Please select a repayment type.");
    }

    if (!formData.mortgageTerm || Number(formData.mortgageTerm) <= 0) {
      nextErrors.push("Please enter the mortgage term.");
    }

    if (propertyValueNum > 0 && ltv > 90) {
      nextErrors.push(
        `LTV (Loan To Value) should be less than or equal to 90%. Currently it is ${Math.round(
          ltv,
        )}%.`,
      );
    }

    setErrors(nextErrors);
    return nextErrors.length === 0;
  };

  const handleCalculate = () => {
    const isValid = validate();
    setHasCalculated(isValid);
  };

  const handleClear = () => {
    setFormData(initialState);
    setErrors([]);
    setHasCalculated(false);
  };

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <div className="title burgandy">HOW MUCH WILL IT COST?</div>

        {errors.length > 0 && (
          <div
            style={{
              background: "#ffffff",
              marginBottom: "24px",
              border: "1px solid #d9d9d9",
            }}
          >
            <div
              style={{
                background: "var(--cardColor)",
                color: "#fff",
                padding: "12px 16px",
                fontWeight: 700,
                fontSize: "18px",
              }}
            >
              Error!
            </div>
            <div style={{ padding: "16px 20px", color: "var(--burgandy)" }}>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {errors.map((error, index) => (
                  <li key={index} style={{ marginBottom: "8px" }}>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">
            Reason for mortgage
          </div>
          <div className="select-wrapper">
            {/* <select
              name="reasonForMortgage"
              className="borrow-input"
              value={formData.reasonForMortgage}
              onChange={handleChange}
            >
              {reasonOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="reasonForMortgage"
              options={reasonOptions}
              value={formData.reasonForMortgage}
              onChange={handleChange}
              placeholder="Please select a reason"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">
            Property value (£)
          </div>
          <FormattedNumberInput
            name="propertyValue"
            value={formData.propertyValue}
            onChange={(raw) =>
              setFormData((prev) => ({
                ...prev,
                propertyValue: raw,
              }))
            }
            className="borrow-input  content"
            placeholder=""
          />
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">
            How much deposit you have? (£)
          </div>
          <div className="select-wrapper">
            <FormattedNumberInput
              name="deposit"
              value={formData.deposit}
              onChange={(raw) =>
                setFormData((prev) => ({
                  ...prev,
                  deposit: raw,
                }))
              }
              className="borrow-input  content"
              placeholder=""
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">
            How much do you want to borrow? (£)
          </div>
          <div className="select-wrapper">
            <input
              type="text"
              name="amountToBorrow"
              className="borrow-input content"
              value={amountToBorrowNum ? formatNumber(amountToBorrowNum) : ""}
              readOnly
              style={{
                background: "#F3F3F3",
                color: "#73151F",
              }}
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">Repayment type</div>
          <div className="select-wrapper">
            {/* <select
              name="repaymentType"
              className="borrow-input"
              value={formData.repaymentType}
              onChange={handleChange}
            >
              {repaymentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select> */}
            <CustomSelect
              name="repaymentType"
              options={repaymentOptions}
              value={formData.repaymentType}
              onChange={handleChange}
              placeholder="Please select a repayment type"
            />
          </div>
        </div>

        <div className="borrow-field-group">
          <div className="borrow-label content burgandy align-left">Mortgage term (years)</div>
          <input
            type="text"
            name="mortgageTerm"
            className="borrow-input content"
            value={formData.mortgageTerm}
            onChange={handleChange}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 16,
            marginTop: 24,
            justifyContent: "flex-end",
          }}
        >
          <SecondaryButton onClick={handleClear} text="Clear" />
          <PrimaryButton onClick={handleCalculate} text="Calculate" />
        </div>
      </div>

      {hasCalculated && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <div className="result-section">
              <div className="result-heading">Mortgage payment per month</div>
              <div className="result-value">
                {formatCurrency(monthlyPayment, 0)}
              </div>
              <p className="result-copy">
                Over {Number(formData.mortgageTerm || 0)} years, based on a{" "}
                <strong>
                  {formData.repaymentType === "interest_only"
                    ? "interest only"
                    : "repayment"}
                </strong>{" "}
                mortgage.
              </p>
            </div>

            <div className="result-divider" />

            <div className="result-section" style={{ marginBottom: 0 }}>
              <p className="result-copy">
                Based on LTV of <strong>{Math.round(ltv)}%</strong> and interest
                rate of <strong>{interestRate}%</strong>.
              </p>
              {formData.repaymentType === "interest_only" && (
                <p className="result-copy">
                  With a balance to pay at the end of the terms of{" "}
                  <strong>{formatCurrency(amountToBorrowNum)}</strong>.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
